// AdminUsagePanel.jsx
// Settings → Platform. The deployment operator's view of tenants, users and LLM
// spend (P0.33 33.B.1/33.B.2/33.B.3).
//
// ITS OWN SURFACE, NOT A TAB INSIDE THE LOG VIEWER (33.B.6, decided 25 Oct).
// LogViewerPanel is owner-scoped and per-tenant; this is cross-tenant and
// system-admin only. Putting requireOwner and requireSystemAdmin data on one
// screen is the confusion api/admin/users/approve.js already made once, where a
// gate proving "admin of SOME tenant" was mistaken for "admin of THIS tenant".
//
// THE GATE IS THE BACKEND, NOT THIS FILE.
// ProfileSettingsPage hides the tab for non-system-admins as a courtesy.
// requireSystemAdmin on /api/admin/users/list and /api/admin/usage/summary is
// the control, and 403s regardless of what renders.
//
// 33.B.1 — AND THE GATE IS STATED ON THE SCREEN, which the sibling
// DataRequestsPanel does only in a source comment. System admin is an email
// address in SYSTEM_ADMIN_EMAILS, a Vercel environment variable: it is not a
// database role, it does not follow a tenant swap, and it cannot be granted from
// inside the product. Anyone reading this page needs to know its audience is
// managed somewhere they may not have access to, so the note is rendered rather
// than left in the code.

import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import './AdminUsagePanel.css';

const PERIODS = [7, 30, 90];

function fmtUsd(n) {
  if (n === null || n === undefined) return '—';
  // Sub-cent figures are normal here: a Haiku call costs fractions of a cent, so
  // rounding to 2dp would render most real rows as $0.00 and look broken.
  if (n > 0 && n < 0.01) return '<$0.01';
  return '$' + n.toFixed(2);
}

function fmtInt(n) {
  return (n === null || n === undefined) ? '—' : n.toLocaleString();
}

function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleDateString(undefined,
    { day: 'numeric', month: 'short', year: 'numeric' }) : 'never';
}

function fmtMs(n) {
  if (n === null || n === undefined) return '—';
  return n >= 1000 ? (n / 1000).toFixed(1) + 's' : n + 'ms';
}

export default function AdminUsagePanel() {
  const [days, setDays]       = useState(30);
  const [usage, setUsage]     = useState(null);
  const [roster, setRoster]   = useState(null);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [uRes, rRes] = await Promise.all([
        apiFetch('/api/admin/usage/summary?days=' + days),
        apiFetch('/api/admin/users/list'),
      ]);
      // 403 arrives as an ordinary response: src/lib/api.js fires onAuthFailure
      // on 401 but deliberately not on 403, because "authenticated but not
      // permitted" is not a session problem. Same reasoning as LogViewerPanel.
      if (uRes.status === 403 || rRes.status === 403) {
        setError('This page is for the deployment system administrator '
          + '(SYSTEM_ADMIN_EMAILS). Your account is not one.');
        setUsage(null); setRoster(null);
        return;
      }
      const u = await uRes.json();
      const r = await rRes.json();
      if (!u.success) throw new Error(u.error || 'Could not load usage');
      if (!r.success) throw new Error(r.error || 'Could not load users');
      setUsage(u); setRoster(r);
    } catch (e) {
      setError(e.message || 'Could not load the platform view');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="admin-usage">
      <header className="admin-usage__head">
        <h3>Platform</h3>
        {/* 33.B.1 — the audience, on the screen. */}
        <p className="admin-usage__gate">
          Visible to the deployment’s system administrators, set by{' '}
          <code>SYSTEM_ADMIN_EMAILS</code> in Vercel — not a role in this product,
          so it cannot be granted here and does not change with a tenant swap.
        </p>
      </header>

      {error && <p className="admin-usage__error">{error}</p>}
      {loading && !error && <p className="admin-usage__muted">Loading…</p>}

      {usage && (
        <section className="admin-usage__section">
          <div className="admin-usage__sectionhead">
            <h4>LLM usage</h4>
            <div className="admin-usage__periods">
              {PERIODS.map(d => (
                <button
                  key={d}
                  type="button"
                  className={d === days ? 'is-active' : ''}
                  onClick={() => setDays(d)}
                >{d}d</button>
              ))}
            </div>
          </div>

          {/* Both of these say the figures are INCOMPLETE. Shown prominently
              rather than as a footnote: a cost total that silently omits
              unpriced calls, or covers only part of the window, is the quiet
              undercount that reads as low spend instead of as missing
              configuration. */}
          {!usage.totals.costComplete && (
            <p className="admin-usage__warn">
              {fmtInt(usage.totals.unpricedCalls)} call(s) ran on a model with no
              price configured, so the cost below is incomplete. Add the model to{' '}
              <code>llm_prices</code>.
            </p>
          )}
          {usage.capped && (
            <p className="admin-usage__warn">
              Showing the most recent {fmtInt(usage.rowCap)} calls in this window —
              there are more.
            </p>
          )}

          <div className="admin-usage__totals">
            <div><span>{fmtUsd(usage.totals.costUsd)}</span><label>cost</label></div>
            <div><span>{fmtInt(usage.totals.calls)}</span><label>calls</label></div>
            <div><span>{fmtInt(usage.totals.inputTokens)}</span><label>input tokens</label></div>
            <div><span>{fmtInt(usage.totals.outputTokens)}</span><label>output tokens</label></div>
            <div><span>{fmtInt(usage.totals.failed)}</span><label>failed</label></div>
            <div><span>{fmtInt(usage.totals.truncated)}</span><label>truncated</label></div>
            <div><span>{fmtMs(usage.totals.p95DurationMs)}</span><label>p95</label></div>
          </div>

          <h5>By tenant</h5>
          <table className="admin-usage__table">
            <thead>
              <tr><th>Tenant</th><th>Calls</th><th>In</th><th>Out</th><th>Cost</th><th>Failed</th></tr>
            </thead>
            <tbody>
              {usage.tenants.map(t => (
                <tr key={t.tenantId || 'unattributed'}
                    className={t.unattributed ? 'is-unattributed' : ''}>
                  <td>{t.name}{!t.costComplete && <span className="admin-usage__flag" title="some calls are unpriced"> ⚠</span>}</td>
                  <td>{fmtInt(t.calls)}</td>
                  <td>{fmtInt(t.inputTokens)}</td>
                  <td>{fmtInt(t.outputTokens)}</td>
                  <td>{fmtUsd(t.costUsd)}</td>
                  <td>{fmtInt(t.failed)}</td>
                </tr>
              ))}
              {!usage.tenants.length && (
                <tr><td colSpan={6} className="admin-usage__muted">No calls recorded in this window.</td></tr>
              )}
            </tbody>
          </table>

          <h5>By surface</h5>
          <table className="admin-usage__table">
            <thead>
              <tr><th>Surface</th><th>Calls</th><th>Cost</th><th>Truncated</th><th>p50</th><th>p95</th></tr>
            </thead>
            <tbody>
              {usage.bySurface.map(s => (
                <tr key={s.key}>
                  <td>{s.key}</td>
                  <td>{fmtInt(s.calls)}</td>
                  <td>{fmtUsd(s.costUsd)}</td>
                  <td>{fmtInt(s.truncated)}</td>
                  <td>{fmtMs(s.p50DurationMs)}</td>
                  <td>{fmtMs(s.p95DurationMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h5>By model</h5>
          <table className="admin-usage__table">
            <thead><tr><th>Model</th><th>Calls</th><th>In</th><th>Out</th><th>Cost</th></tr></thead>
            <tbody>
              {usage.byModel.map(m => (
                <tr key={m.key}>
                  <td>{m.key}{!m.costComplete && <span className="admin-usage__flag" title="no price configured"> ⚠</span>}</td>
                  <td>{fmtInt(m.calls)}</td>
                  <td>{fmtInt(m.inputTokens)}</td>
                  <td>{fmtInt(m.outputTokens)}</td>
                  <td>{fmtUsd(m.costUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {roster && (
        <section className="admin-usage__section">
          <h4>Tenants and users</h4>
          {/* Said out loud rather than implied: a roster that pages silently is
              worse than ad-hoc SQL, which at least showed its own LIMIT. */}
          {!roster.totals.authListComplete && (
            <p className="admin-usage__warn">
              The sign-in lookup returned a full page, so some users’ last sign-in
              may be missing. This list may be incomplete.
            </p>
          )}
          {!!roster.orphanedUsers.length && (
            <p className="admin-usage__warn">
              {roster.orphanedUsers.length} user(s) belong to a tenant that no
              longer exists: {roster.orphanedUsers.map(u => u.email || u.id).join(', ')}
            </p>
          )}
          <p className="admin-usage__muted">
            {fmtInt(roster.totals.tenants)} tenants · {fmtInt(roster.totals.users)} users
            {roster.totals.pendingApproval > 0
              && ' · ' + fmtInt(roster.totals.pendingApproval) + ' awaiting approval'}
          </p>

          {roster.tenants.map(t => (
            <div key={t.id} className="admin-usage__tenant">
              <h5>
                {t.companyName || t.name}
                {t.isHoldingTenant && <span className="admin-usage__badge">holding — not yet approved</span>}
                {!t.active && <span className="admin-usage__badge">inactive</span>}
              </h5>
              <table className="admin-usage__table">
                <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Active</th><th>Last sign-in</th></tr></thead>
                <tbody>
                  {t.users.map(u => (
                    <tr key={u.id}>
                      <td>{u.email || <span className="admin-usage__muted">—</span>}</td>
                      <td>{u.fullName || '—'}</td>
                      <td>{u.role}</td>
                      <td>{u.active ? 'yes' : 'no'}</td>
                      <td>
                        {fmtDate(u.lastSignInAt)}
                        {/* "never" and "we could not look it up" are different
                            facts, and a bare dash conflates them. */}
                        {!u.authRecordFound && <span className="admin-usage__flag" title="no auth record found"> ⚠</span>}
                      </td>
                    </tr>
                  ))}
                  {!t.users.length && (
                    <tr><td colSpan={5} className="admin-usage__muted">No users.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
